/**
 * live_status v1: the reference renderer (docs/contracts/unit-live-status.md §5).
 *
 * Pure and framework-free, so App web's UnitStatusGlyphs and Sales (which copies this file
 * as is) draw identical glyphs, tones and labels. The server classifies; this only maps
 * the classified values to what is shown. It never computes a battery band or a rule.
 * Pinned by docs/contracts/unit-live-status-golden.json.
 */

export type BatteryBand = 'healthy' | 'warning' | 'critical' | 'unknown' | 'not_installed';
/** Closed enum, frozen 2026-10-30. From the current battery power with a ±25 W idle band. */
export type BatteryDirection = 'charging' | 'draining' | 'idle' | 'unknown' | 'not_installed';
export type ChargeSource = 'solar' | 'shore' | 'shore_likely' | 'none' | 'unknown' | 'not_installed';
export type EvState = 'charging' | 'idle' | 'unknown' | 'not_installed';
export type HvacState = 'on' | 'off' | 'unknown' | 'not_installed';
export type HvacMode = 'cooling' | 'heating' | 'fan_only' | 'off';
export type StarlinkState = 'online' | 'idle' | 'degraded' | 'suspended' | 'offline' | 'unknown' | 'not_installed';

export interface UnitLiveStatus {
  version: 1;
  battery: {
    band: BatteryBand; soc: number | null; runway_minutes?: number | null; charge_minutes?: number | null;
    /** Additive (2026-10-30); a server from before it leaves it out, which reads as unknown. */
    direction?: BatteryDirection;
    /** Additive (2026-11-04): at or below the deeper line (15%): show the Critical label. Absent reads false. */
    critical_low?: boolean;
  };
  charge: { source: ChargeSource; power_w?: number | null };
  /** The panels' own output (additive, 2026-10-30); absent from older servers, absent reads as null. */
  solar?: { producing?: boolean | null; power_w?: number | null };
  ev: { state: EvState; load_w?: number | null };
  hvac: { state: HvacState; mode?: HvacMode | null };
  starlink: { state: StarlinkState; offline_minutes?: number | null };
  stale: { is_stale: boolean; minutes_ago?: number | null; last_update_at?: string | null };
}

export type LiveStatusSlotName = 'battery' | 'charge' | 'ev' | 'hvac' | 'starlink' | 'stale';
export type LiveStatusGlyph =
  | 'battery_pill' | 'sun' | 'plug' | 'plug_outline' | 'ev_plug' | 'fan' | 'dish' | 'dish_off' | 'clock';
export type LiveStatusTone = 'green' | 'amber' | 'coral' | 'blue' | 'cyan' | 'neutral' | 'muted' | 'off';

export type LiveStatusSlot =
  | { slot: LiveStatusSlotName; hidden: true; glyph?: never; tone?: never; label?: never; text?: never }
  | { slot: LiveStatusSlotName; hidden?: never; glyph: LiveStatusGlyph; tone: LiveStatusTone; label: string; text?: string };

/** Fixed order, same meaning everywhere. */
export const LIVE_STATUS_SLOT_ORDER: readonly LiveStatusSlotName[] = Object.freeze(['battery', 'charge', 'ev', 'hvac', 'starlink', 'stale']);

/** Dark-mode colours. `neutral`, `muted` and `off` are the theme foreground at 100%, 45% and 25%. */
export const LIVE_STATUS_TONES = Object.freeze({
  green: '#8FB560',
  amber: '#D6A44A',
  coral: '#E0556C',
  blue: '#5B9ED6',
  cyan: '#41BDC8',
} as const);

const isCount = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n >= 0;

/** 47 min · 4h 10m · 3h · 2d 2h · 2d (whole minutes in). */
export function formatDuration(minutes: number): string {
  const m = Math.max(0, Math.floor(minutes));
  if (m < 60) return `${m} min`;
  if (m < 1440) {
    const h = Math.floor(m / 60);
    const r = m % 60;
    return r ? `${h}h ${r}m` : `${h}h`;
  }
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  return h ? `${d}d ${h}h` : `${d}d`;
}

/** 850 W · 1.5 kW · 7 kW. Tenths of a kW rounded half up on whole numbers (no float toFixed). */
export function formatPower(watts: number): string {
  const w = Math.max(0, Math.round(watts));
  if (w < 1000) return `${w} W`;
  const tenths = Math.floor((w + 50) / 100);
  const whole = Math.floor(tenths / 10);
  const decimal = tenths % 10;
  return decimal ? `${whole}.${decimal} kW` : `${whole} kW`;
}

const withSuffix = (base: string, suffix: string | null) => (suffix ? `${base} · ${suffix}` : base);
const hidden = (slot: LiveStatusSlotName): LiveStatusSlot => ({ slot, hidden: true });

const BAND_TONE: Partial<Record<BatteryBand, LiveStatusTone>> = { healthy: 'green', warning: 'amber', critical: 'coral' };

function batterySlot(b: UnitLiveStatus['battery']): LiveStatusSlot {
  if (b.band === 'not_installed') return hidden('battery');
  const tone = BAND_TONE[b.band];
  // A band without a percentage, or a band this renderer doesn't know, is "no reading".
  if (!tone || !isCount(b.soc)) {
    return { slot: 'battery', glyph: 'battery_pill', tone: 'off', text: '—', label: 'Battery: no current reading' };
  }
  const soc = Math.round(b.soc);
  const eta = isCount(b.runway_minutes)
    ? `${formatDuration(b.runway_minutes)} left`
    : isCount(b.charge_minutes) ? `full in ${formatDuration(b.charge_minutes)}` : null;
  return { slot: 'battery', glyph: 'battery_pill', tone, text: `${soc}%`, label: withSuffix(`Battery ${soc}%`, eta) };
}

function chargeSlot(c: UnitLiveStatus['charge']): LiveStatusSlot {
  const power = isCount(c.power_w) ? formatPower(c.power_w) : null;
  switch (c.source) {
    case 'not_installed': return hidden('charge');
    case 'solar': return { slot: 'charge', glyph: 'sun', tone: 'amber', label: withSuffix('Charging from solar', power) };
    case 'shore': return { slot: 'charge', glyph: 'plug', tone: 'amber', label: withSuffix('Charging from shore power', power) };
    case 'shore_likely': return { slot: 'charge', glyph: 'plug_outline', tone: 'muted', label: 'Probably on shore power' };
    case 'none': return { slot: 'charge', glyph: 'sun', tone: 'off', label: 'Not charging' };
    default: return { slot: 'charge', glyph: 'sun', tone: 'off', label: 'Charging: no current reading' };
  }
}

function evSlot(e: UnitLiveStatus['ev']): LiveStatusSlot {
  if (e.state === 'not_installed') return hidden('ev');
  if (e.state === 'charging') {
    return { slot: 'ev', glyph: 'ev_plug', tone: 'blue', label: withSuffix('EV charging', isCount(e.load_w) ? formatPower(e.load_w) : null) };
  }
  if (e.state === 'idle') return { slot: 'ev', glyph: 'ev_plug', tone: 'off', label: 'EV charger idle' };
  return { slot: 'ev', glyph: 'ev_plug', tone: 'off', label: 'EV charger: no current reading' };
}

const HVAC_MODE_WORD: Record<string, string> = { cooling: 'Cooling', heating: 'Heating', fan_only: 'Fan only' };

function hvacSlot(h: UnitLiveStatus['hvac']): LiveStatusSlot {
  if (h.state === 'not_installed') return hidden('hvac');
  if (h.state === 'on') return { slot: 'hvac', glyph: 'fan', tone: 'cyan', label: withSuffix('HVAC on', HVAC_MODE_WORD[h.mode ?? ''] ?? null) };
  if (h.state === 'off') return { slot: 'hvac', glyph: 'fan', tone: 'off', label: 'HVAC off' };
  return { slot: 'hvac', glyph: 'fan', tone: 'off', label: 'HVAC: no reading' };
}

function starlinkSlot(s: UnitLiveStatus['starlink']): LiveStatusSlot {
  switch (s.state) {
    case 'not_installed': return hidden('starlink');
    case 'online': return { slot: 'starlink', glyph: 'dish', tone: 'green', label: 'Starlink online' };
    case 'idle': return { slot: 'starlink', glyph: 'dish', tone: 'amber', label: 'Starlink idle' };
    case 'degraded': return { slot: 'starlink', glyph: 'dish', tone: 'amber', label: 'Starlink degraded' };
    case 'suspended': return { slot: 'starlink', glyph: 'dish', tone: 'amber', label: 'Starlink suspended' };
    case 'offline':
      return { slot: 'starlink', glyph: 'dish_off', tone: 'coral', label: withSuffix('Starlink offline', isCount(s.offline_minutes) ? formatDuration(s.offline_minutes) : null) };
    default: return { slot: 'starlink', glyph: 'dish', tone: 'off', label: 'Starlink: no reading' };
  }
}

function staleSlot(s: UnitLiveStatus['stale']): LiveStatusSlot {
  if (s.is_stale !== true) return hidden('stale');
  const label = isCount(s.minutes_ago) ? `Last update ${formatDuration(s.minutes_ago)} ago` : 'Last update time unknown';
  return { slot: 'stale', glyph: 'clock', tone: 'amber', label };
}

export interface BatteryDirectionMarkView {
  mark: '▲' | '▼';
  tone: LiveStatusTone;
  label: 'Charging' | 'Draining';
}

/**
 * The small mark beside the battery text (Eric, 2026-10-30, mock approved): ▲ in the battery's
 * own tone while charging, ▼ muted while draining, nothing for idle / unknown / not_installed /
 * a missing field, and never beside "—" (only beside a real reading). Not a slot.
 */
export function batteryDirectionMark(status: UnitLiveStatus | null | undefined): BatteryDirectionMarkView | null {
  if (!status || status.version !== 1) return null;
  const slot = batterySlot(status.battery);
  if (slot.hidden || slot.text === '—') return null;
  if (status.battery.direction === 'charging') return { mark: '▲', tone: slot.tone, label: 'Charging' };
  if (status.battery.direction === 'draining') return { mark: '▼', tone: 'muted', label: 'Draining' };
  return null;
}

export interface BatteryCriticalLabelView {
  text: 'Critical';
  tone: 'coral';
  label: 'Critical';
}

/**
 * The small coral "Critical" label beside the battery (Eric, 2026-11-04: "15% or less is
 * critical"): only for a real critical reading the server marked critical_low. Never beside
 * "—", for a version this client doesn't know, or for a stray flag on another band. Not a slot.
 */
export function batteryCriticalLabel(status: UnitLiveStatus | null | undefined): BatteryCriticalLabelView | null {
  if (!status || status.version !== 1) return null;
  const { battery } = status;
  if (battery.critical_low !== true || battery.band !== 'critical') return null;
  if (batterySlot(battery).text === '—') return null;
  return { text: 'Critical', tone: 'coral', label: 'Critical' };
}

/**
 * The six slots in their fixed order. No live_status (an older App, or none for this
 * trailer) gives no slots: show no icons rather than guessed ones.
 */
export function liveStatusSlots(status: UnitLiveStatus | null | undefined): LiveStatusSlot[] {
  if (!status || status.version !== 1) return [];
  return [
    batterySlot(status.battery),
    chargeSlot(status.charge),
    evSlot(status.ev),
    hvacSlot(status.hvac),
    starlinkSlot(status.starlink),
    staleSlot(status.stale),
  ];
}
